import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { 
  Activity, Clock, Target, DollarSign, AlertCircle, FileText, 
  ChevronRight, CheckCircle, Users, TrendingUp, Shield
} from 'lucide-react';

// Warm Premium Palette for Admin
const colors = {
  primaryTeal: '#3D6B67',
  primaryTealDark: '#2F5652',
  bgDark: '#1A2F2B',
  bgCard: 'rgba(61, 107, 103, 0.15)',
  bgCardHover: 'rgba(61, 107, 103, 0.25)',
  gold: '#D6B15E',
  goldHover: '#E2C77A',
  textHeading: '#FFFFFF',
  textBody: '#E8E4E1',
  textMuted: '#A8A29D',
  borderColor: 'rgba(61, 107, 103, 0.3)',
  success: '#4D9078',
  warning: '#F59E0B',
  danger: '#EF4444',
};

// Skeleton Components for Admin Dashboard
const SkeletonPulse = ({ className = '', style = {} }) => (
  <div 
    className={`animate-pulse rounded-xl ${className}`}
    style={{ backgroundColor: 'rgba(255,255,255,0.1)', ...style }}
  />
);

const AdminSkeletonStatCard = () => (
  <div 
    className="rounded-xl p-6"
    style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderColor}` }}
  >
    <div className="flex items-center justify-between mb-2">
      <SkeletonPulse className="w-12 h-12 rounded-full" />
    </div>
    <SkeletonPulse className="h-8 w-24 mb-2" />
    <SkeletonPulse className="h-4 w-28" />
  </div>
);

const AdminSkeletonEngagementCard = () => (
  <div 
    className="p-4 rounded-lg"
    style={{ backgroundColor: 'rgba(14, 30, 25, 0.5)', border: `1px solid ${colors.borderColor}` }}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <SkeletonPulse className="h-5 w-48 mb-2" />
        <SkeletonPulse className="h-4 w-64 mb-3" />
        <div className="flex items-center gap-4">
          <SkeletonPulse className="h-4 w-24" />
          <SkeletonPulse className="h-4 w-20" />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <SkeletonPulse className="h-6 w-20 rounded-full" />
        <SkeletonPulse className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const AdminDashboardSkeleton = () => (
  <div className="min-h-screen" style={{ backgroundColor: colors.bgDark }}>
    <Header />
    
    {/* Hero Skeleton */}
    <section 
      className="relative pt-28 pb-8 px-4 md:px-8"
      style={{ background: `linear-gradient(135deg, ${colors.primaryTeal} 0%, ${colors.primaryTealDark} 50%, ${colors.bgDark} 100%)` }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <SkeletonPulse className="h-8 w-80 mb-3" />
            <SkeletonPulse className="h-5 w-56" />
          </div>
          <div className="flex gap-3">
            <SkeletonPulse className="h-12 w-32" />
            <SkeletonPulse className="h-12 w-36" />
            <SkeletonPulse className="h-12 w-40" />
          </div>
        </div>
      </div>
    </section>

    <div className="py-8 px-4 md:px-8" style={{ backgroundColor: colors.primaryTealDark }}>
      <div className="max-w-7xl mx-auto">
        {/* Stats Grid Skeleton */}
        <div className="grid md:grid-cols-5 gap-6 mb-8">
          {[1, 2, 3, 4, 5].map((i) => (
            <AdminSkeletonStatCard key={i} />
          ))}
        </div>

        {/* Tabs Skeleton */}
        <div className="mb-6 flex gap-6" style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
          <SkeletonPulse className="h-6 w-24 mb-3" />
          <SkeletonPulse className="h-6 w-36 mb-3" />
          <SkeletonPulse className="h-6 w-32 mb-3" />
        </div>

        {/* Content Skeleton */}
        <div className="space-y-6">
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderColor}` }}
          >
            <SkeletonPulse className="h-6 w-48 mb-6" />
            <div className="grid md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div 
                  key={i}
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <SkeletonPulse className="w-5 h-5" />
                    <SkeletonPulse className="h-4 w-20" />
                  </div>
                  <SkeletonPulse className="h-8 w-16 mb-1" />
                  <SkeletonPulse className="h-3 w-14" />
                </div>
              ))}
            </div>
          </div>

          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.borderColor}` }}
          >
            <SkeletonPulse className="h-6 w-44 mb-6" />
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <AdminSkeletonEngagementCard key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SaaSAdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    const token = localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('xchange_user');

    if (!token || !userData) {
      navigate('/xchange/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'admin') {
      navigate('/xchange/login');
      return;
    }

    setUser(parsedUser);
    await fetchDashboardData(token);
  };

  const fetchDashboardData = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

      const statsRes = await fetch(`${backendUrl}/api/saas/dashboard/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsRes.json();
      setDashboardStats(statsData);

      const engagementsRes = await fetch(`${backendUrl}/api/saas/engagements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const engagementsData = await engagementsRes.json();
      setEngagements(engagementsData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'on_track': return colors.gold;
      case 'at_risk': return '#F59E0B';
      case 'off_track': return '#EF4444';
      default: return colors.textMuted;
    }
  };

  const getHealthLabel = (health) => {
    switch (health) {
      case 'on_track': return 'On Track';
      case 'at_risk': return 'At Risk';
      case 'off_track': return 'Off Track';
      default: return health;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return <AdminDashboardSkeleton />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgDark }}>
      <Header />

      {/* Hero Section */}
      <section 
        className="relative pt-28 pb-8 px-4 md:px-8"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primaryTeal} 0%, ${colors.primaryTealDark} 50%, ${colors.bgDark} 100%)`
        }}
      >
        <div 
          className="absolute inset-0 opacity-5"
          style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: colors.textHeading }}>
                Admin Dashboard - Project Management
              </h1>
              <p style={{ color: colors.textBody }}>Platform-wide oversight and monitoring</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => navigate('/saas/admin/analytics')}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.textHeading, border: '1.5px solid rgba(255, 255, 255, 0.35)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; e.currentTarget.style.borderColor = colors.gold; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textHeading; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)'; }}
              >
                View Analytics
              </button>
              <button
                onClick={() => navigate('/saas/admin/reports')}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.textHeading, border: '1.5px solid rgba(255, 255, 255, 0.35)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; e.currentTarget.style.borderColor = colors.gold; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = colors.textHeading; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)'; }}
              >
                Generate Reports
              </button>
              <button
                onClick={() => navigate('/saas/admin/manage')}
                className="px-6 py-3 rounded-lg font-medium transition-all duration-300"
                style={{ backgroundColor: '#0E1E19', color: '#FFFFFF', border: '1px solid rgba(255, 255, 255, 0.2)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.gold; e.currentTarget.style.color = '#0E1E19'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#0E1E19'; e.currentTarget.style.color = '#FFFFFF'; }}
              >
                Create Engagement
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="py-8 px-4 md:px-8" style={{ backgroundColor: colors.primaryTealDark }}>
        <div className="max-w-7xl mx-auto">
          {/* Stats Grid */}
          {dashboardStats && (
            <div className="grid md:grid-cols-5 gap-6 mb-8">
              {[
                { icon: Activity, label: 'Total Engagements', value: dashboardStats.totalEngagements },
                { icon: CheckCircle, label: 'Active Projects', value: dashboardStats.activeEngagements },
                { icon: AlertCircle, label: 'Pending Approvals', value: dashboardStats.pendingApprovals },
                { icon: DollarSign, label: 'Total Deployed', value: formatCurrency(dashboardStats.totalValueDeployed) },
                { icon: TrendingUp, label: 'Platform Health', value: `${dashboardStats.avgHealthScore}%` }
              ].map((item, idx) => (
                <div 
                  key={idx}
                  className="rounded-xl p-6 transition-all duration-300"
                  style={{ 
                    backgroundColor: 'rgba(14, 30, 25, 0.4)',
                    border: '1px solid rgba(151, 182, 167, 0.2)',
                    backdropFilter: 'blur(8px)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.gold;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(151, 182, 167, 0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(214, 177, 94, 0.15)' }}>
                      <item.icon className="w-6 h-6" style={{ color: colors.gold }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold mb-1" style={{ color: colors.textHeading }}>{item.value}</div>
                  <div className="text-sm" style={{ color: colors.textMuted }}>{item.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6 flex gap-6" style={{ borderBottom: '1px solid rgba(151, 182, 167, 0.2)' }}>
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'engagements', label: `All Engagements (${engagements.length})` },
              { id: 'approvals', label: 'Pending Approvals' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="pb-3 px-1 font-medium transition-all"
                style={{ 
                  color: activeTab === tab.id ? colors.gold : colors.textMuted,
                  borderBottom: activeTab === tab.id ? `2px solid ${colors.gold}` : '2px solid transparent'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Health Distribution */}
              <div 
                className="rounded-xl p-6"
                style={{ 
                  backgroundColor: 'rgba(14, 30, 25, 0.4)',
                  border: '1px solid rgba(151, 182, 167, 0.2)',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textHeading }}>
                  Platform Health Overview
                </h3>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { status: 'on_track', icon: CheckCircle, label: 'On Track', color: colors.gold },
                    { status: 'at_risk', icon: AlertCircle, label: 'At Risk', color: '#F59E0B' },
                    { status: 'off_track', icon: AlertCircle, label: 'Off Track', color: '#EF4444' }
                  ].map((item, idx) => (
                    <div 
                      key={idx}
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: `${item.color}15`, border: `1px solid ${item.color}30` }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <item.icon className="w-5 h-5" style={{ color: item.color }} />
                        <span className="font-semibold" style={{ color: item.color }}>{item.label}</span>
                      </div>
                      <div className="text-2xl font-bold" style={{ color: colors.textHeading }}>
                        {engagements.filter(e => e.healthStatus === item.status).length}
                      </div>
                      <div className="text-sm" style={{ color: colors.textMuted }}>projects</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Engagements */}
              <div 
                className="rounded-xl p-6"
                style={{ 
                  backgroundColor: 'rgba(14, 30, 25, 0.4)',
                  border: '1px solid rgba(151, 182, 167, 0.2)',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textHeading }}>
                  Recent Engagements
                </h3>
                
                {engagements.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
                    <p style={{ color: colors.textBody }}>No engagements yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {engagements.slice(0, 5).map((engagement) => (
                      <div 
                        key={engagement.id || engagement.engagementId}
                        className="p-4 rounded-lg cursor-pointer transition-all duration-300"
                        style={{ 
                          backgroundColor: 'rgba(14, 30, 25, 0.5)',
                          border: '1px solid rgba(151, 182, 167, 0.2)'
                        }}
                        onClick={() => navigate(`/saas/engagement/${engagement.id || engagement.engagementId}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = colors.gold;
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(151, 182, 167, 0.2)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold mb-1" style={{ color: colors.textHeading }}>
                              {engagement.projectTitle || engagement.intentTitle}
                            </h4>
                            <p className="text-sm mb-2" style={{ color: colors.textMuted }}>
                              {engagement.companyName || engagement.sponsorName} → {engagement.agencyName}
                            </p>
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1" style={{ color: colors.textMuted }}>
                                <DollarSign className="w-4 h-4" />
                                <span>{formatCurrency(engagement.totalBudget)}</span>
                              </div>
                              <div className="flex items-center gap-1" style={{ color: colors.textMuted }}>
                                <Clock className="w-4 h-4" />
                                <span>{engagement.duration} months</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span 
                              className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${getHealthColor(engagement.healthStatus)}15`,
                                color: getHealthColor(engagement.healthStatus)
                              }}
                            >
                              {getHealthLabel(engagement.healthStatus)}
                            </span>
                            <ChevronRight className="w-5 h-5" style={{ color: colors.textMuted }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'engagements' && (
            <div 
              className="rounded-xl p-6"
              style={{ 
                backgroundColor: 'rgba(14, 30, 25, 0.4)',
                border: '1px solid rgba(151, 182, 167, 0.2)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <h3 className="text-xl font-semibold mb-6" style={{ color: colors.textHeading }}>
                All Platform Engagements
              </h3>
              
              {engagements.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-16 h-16 mx-auto mb-4" style={{ color: colors.textMuted }} />
                  <p style={{ color: colors.textBody }}>No engagements yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {engagements.map((engagement) => (
                    <div 
                      key={engagement.id || engagement.engagementId}
                      className="p-5 rounded-lg cursor-pointer transition-all duration-300"
                      style={{ 
                        backgroundColor: 'rgba(14, 30, 25, 0.5)',
                        border: '1px solid rgba(151, 182, 167, 0.2)'
                      }}
                      onClick={() => navigate(`/saas/engagement/${engagement.id || engagement.engagementId}`)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = colors.gold;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(151, 182, 167, 0.2)';
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg mb-1" style={{ color: colors.textHeading }}>
                            {engagement.projectTitle || engagement.intentTitle}
                          </h4>
                          <p style={{ color: colors.textMuted }}>
                            {engagement.companyName || engagement.sponsorName} → {engagement.agencyName}
                          </p>
                        </div>
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${getHealthColor(engagement.healthStatus)}15`,
                            color: getHealthColor(engagement.healthStatus)
                          }}
                        >
                          {getHealthLabel(engagement.healthStatus)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="mb-1" style={{ color: colors.textMuted }}>Total Budget</div>
                          <div className="font-semibold" style={{ color: colors.textHeading }}>{formatCurrency(engagement.totalBudget)}</div>
                        </div>
                        <div>
                          <div className="mb-1" style={{ color: colors.textMuted }}>Sector</div>
                          <div className="font-semibold" style={{ color: colors.textHeading }}>{engagement.sector || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="mb-1" style={{ color: colors.textMuted }}>Status</div>
                          <div className="font-semibold capitalize" style={{ color: colors.textHeading }}>{engagement.status}</div>
                        </div>
                        <div className="flex items-center justify-end">
                          <button 
                            className="font-medium flex items-center gap-1 transition-colors"
                            style={{ color: colors.gold }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/saas/engagement/${engagement.id || engagement.engagementId}`);
                            }}
                          >
                            View Details
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'approvals' && (
            <div 
              className="rounded-xl p-6"
              style={{ 
                backgroundColor: 'rgba(14, 30, 25, 0.4)',
                border: '1px solid rgba(151, 182, 167, 0.2)',
                backdropFilter: 'blur(8px)'
              }}
            >
              <h3 className="text-xl font-semibold mb-6" style={{ color: colors.textHeading }}>
                Pending Approvals
              </h3>
              
              <div className="text-center py-12">
                <Shield className="w-16 h-16 mx-auto mb-4" style={{ color: colors.gold }} />
                <p style={{ color: colors.textBody }}>No pending approvals</p>
                <p className="text-sm mt-2" style={{ color: colors.textMuted }}>
                  Milestone completions and disbursement requests will appear here
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaaSAdminDashboard;
